import React, { useState, useEffect } from "react";
import { Modal, Button, Table, Form, Row, Col } from "react-bootstrap";
import {
  doc,
  getDoc,
  updateDoc,
  query,
  collection,
  where,
  getDocs,
  deleteDoc,
} from "firebase/firestore";
import { getStorage, ref, deleteObject } from "firebase/storage";
import { db } from "../FireBase";
import ToastAlert from "../components/ToastAlert";
import Loading from "./Loading";

const ManualUnpaidModal = ({ show, onHide, subscriptionId, schemeId, userId }) => {
  const [payments, setPayments] = useState([]);
  const [scheme, setScheme] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastVariant, setToastVariant] = useState("success");

  useEffect(() => {
    if (show && subscriptionId && schemeId) {
      fetchSchemeDetails();
      fetchSubscriptionDetails();
    }
  }, [show, subscriptionId, schemeId]);

  const fetchSchemeDetails = async () => {
    try {
      const docRef = doc(db, "schemes", schemeId);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        setScheme({ id: docSnap.id, ...docSnap.data() });
      }
    } catch (error) {
      console.error("Error fetching scheme:", error);
    }
  };

  const fetchSubscriptionDetails = async () => {
    try {
      setLoading(true);
      const q = query(
        collection(db, "subscriptions"),
        where("subscriptionId", "==", subscriptionId)
      );
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const subDoc = querySnapshot.docs[0];
        const data = subDoc.data();
        setSubscription({ id: subDoc.id, ...data });
        setPayments(data.payments || []);
        setQuantity(data.quantity || 1);
      } else {
        console.error("No subscription found for subscriptionId:", subscriptionId);
      }
    } catch (error) {
      console.error("Error fetching subscription:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatMonthYear = (monthStr) => {
    const [year, monthNum] = monthStr.split("-");
    const monthName = new Date(`${year}-${monthNum}-01`).toLocaleString("en-US", {
      month: "long",
    });
    return ` ${monthName} - ${year}`;
  };

  // 🔹 Update global quantity
  const handleQuantityChange = async (newQty) => {
    try {
      setLoading(true);
      const q = query(
        collection(db, "subscriptions"),
        where("subscriptionId", "==", subscriptionId)
      );
      const querySnapshot = await getDocs(q);
      if (querySnapshot.empty) return;

      const subDoc = querySnapshot.docs[0];
      const subRef = doc(db, "subscriptions", subDoc.id);
      const subData = subDoc.data();

      const qty = parseInt(newQty);
      const monthlyDue = subData.monthlyDue || 0;
      const updatedMonthlyPayment = monthlyDue * qty;

      await updateDoc(subRef, {
        quantity: qty,
        monthlyPayment: updatedMonthlyPayment,
      });

      setQuantity(qty);
      setSubscription({
        ...subData,
        quantity: qty,
        monthlyPayment: updatedMonthlyPayment,
      });

      setToastMessage(
        `Quantity updated to ${qty}. Monthly payment adjusted to ₹${updatedMonthlyPayment}.`
      );
      setToastVariant("info");
      setShowToast(true);
    } catch (error) {
      console.error("Error updating quantity:", error);
      setToastMessage("Error updating quantity.");
      setToastVariant("danger");
      setShowToast(true);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkUnpaid = async (month) => {
    try {
      setLoading(true);
      const storage = getStorage();

      const formattedMonth = formatMonthYear(month);

      const subQuery = query(
        collection(db, "subscriptions"),
        where("subscriptionId", "==", subscriptionId)
      );
      const subSnap = await getDocs(subQuery);

      if (subSnap.empty) {
        setToastMessage("Subscription not found!");
        setToastVariant("danger");
        setShowToast(true);
        return;
      }

      const subDoc = subSnap.docs[0];
      const subRef = doc(db, "subscriptions", subDoc.id);
      const subData = subDoc.data();

      const updatedPaidMonths = Math.max(subData.paidMonths - 1, 0);
      const updatedPayments = subData.payments.map((p) =>
        p.month === month
          ? {
              ...p,
              paymentStatus: "Pending",
              unpaidTimestamp: new Date().toISOString(),
            }
          : p
      );

      // 🔹 Delete related transactions
      const transQuery = query(
        collection(db, "transactions"),
        where("subscriptionId", "==", subscriptionId),
        where("paidFor", "==", formattedMonth)
      );
      const transSnap = await getDocs(transQuery);

      for (const docSnap of transSnap.docs) {
        const transData = docSnap.data();

        if (transData.receiptURL) {
          try {
            const decodedUrl = decodeURIComponent(transData.receiptURL);
            const startIndex = decodedUrl.indexOf("/o/") + 3;
            const endIndex = decodedUrl.indexOf("?alt=");
            const filePath = decodedUrl.substring(startIndex, endIndex);
            const fileRef = ref(storage, filePath);
            await deleteObject(fileRef);
          } catch (fileErr) {
            console.error("Error deleting receipt file:", fileErr);
          }
        }

        await deleteDoc(doc(db, "transactions", docSnap.id));
      }

      await updateDoc(subRef, {
        payments: updatedPayments,
        paidMonths: updatedPaidMonths,
      });

      setPayments(updatedPayments);
      setToastMessage(`Month ${month} marked as Unpaid. Transaction & receipt deleted.`);
      setToastVariant("warning");
      setShowToast(true);
    } catch (error) {
      console.error("Error marking unpaid:", error);
      setToastMessage("Error updating payment or deleting related files.");
      setToastVariant("danger");
      setShowToast(true);
    } finally {
      setLoading(false);
    }
  };

  const closeModal = () => {
    setPayments([]);
    setScheme(null);
    setSubscription(null);
    onHide();
  };

  return (
    <>
      <ToastAlert
        show={showToast}
        setShow={setShowToast}
        message={toastMessage}
        variant={toastVariant}
      />

      <Modal show={show} onHide={closeModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            Manage Payments — {scheme ? scheme.name : "Loading..."}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loading && <Loading />}

          {subscription && (
            <div className="mb-3 p-3 border rounded bg-light">
              <Row className="align-items-center">
                <Col md={4}>
                  <strong>Quantity:</strong>
                  <Form.Control
                    type="number"
                    min="1"
                    value={quantity}
                    onChange={(e) => handleQuantityChange(e.target.value)}
                    disabled={loading}
                    className="mt-1"
                  />
                </Col>
                <Col md={4}>
                  <strong>Monthly Due:</strong>
                  <div className="mt-1">₹{subscription.monthlyDue}</div>
                </Col>
                <Col md={4}>
                  <strong>Monthly Payment:</strong>
                  <div className="mt-1 fw-bold text-success">
                    ₹{subscription.monthlyPayment || subscription.monthlyDue * quantity}
                  </div>
                </Col>
              </Row>
            </div>
          )}

          {payments.length > 0 ? (
            <Table striped hover responsive>
              <thead>
                <tr>
                  <th>Month</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((p, index) => (
                  <tr key={index}>
                    <td>{p.month}</td>
                    <td>
                      {p.paymentStatus === "Paid" ? (
                        <span className="text-success fw-bold">Paid</span>
                      ) : (
                        <span className="text-danger fw-bold">Pending</span>
                      )}
                    </td>
                    <td>
                      {p.paymentStatus === "Paid" ? (
                        <Button
                          variant="outline-danger"
                          onClick={() => {
                            const confirmChange = window.confirm(
                              `Are you sure you want to mark Month ${p.month} as Unpaid?`
                            );
                            if (confirmChange) handleMarkUnpaid(p.month);
                          }}
                          disabled={loading}
                        >
                          Mark as Unpaid
                        </Button>
                      ) : (
                        <Button variant="secondary" disabled>
                          Already Unpaid
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <p className="text-center text-muted">No payment records found.</p>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default ManualUnpaidModal;
